"use client";

import React, { createContext, useState, useEffect } from 'react';
import ScrollSpy from 'react-scrollspy-navigation';

type Theme = 'light' | 'dark';

interface ThemeContextType {
  theme: Theme | null;
  setTheme: (theme: Theme | null) => void;
  activeSection: string | null;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setTheme] = useState<Theme | null>(null);
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const [themeSections, setThemeSections] = useState<{ id: string; theme: Theme | null }[]>([]);

  // Find all sections with data-theme-change attribute
  useEffect(() => {
    if (typeof document === 'undefined') return;

    const findThemeSections = () => {
      const sections = document.querySelectorAll('section[data-page-nav="true"], section[data-page-nav="false"]');
      const sectionsData = Array.from(sections).map(section => ({
        id: section.id,
        theme: section.getAttribute('data-theme-change') as Theme | null
      }));

      setThemeSections(sectionsData);
    };

    // Initial find with delay to ensure DOM is ready
    const initialTimer = setTimeout(findThemeSections, 100);
    
    // SAFARI FIX: Disable MutationObserver as it causes infinite loops in Safari
    // The initial findThemeSections() call is sufficient for most use cases
    // If sections are added dynamically, they'll be picked up on next navigation
    
    return () => {
      clearTimeout(initialTimer);
    };
  }, []);

  useEffect(() => {
    if (theme) {
      // Add theme-changing class and force theme
      document.body.classList.add('theme-changing');
      document.body.classList.add(`force-${theme}`);

      // Remove theme-changing class after 500ms
      const timeoutId = setTimeout(() => {
        document.body.classList.remove('theme-changing');
      }, 500);

      return () => {
        clearTimeout(timeoutId);
        document.body.classList.remove('theme-changing');
      };
    } else {
      document.body.classList.remove('force-light', 'force-dark', 'theme-changing');
    }
  }, [theme]);

  const handleActiveChange = (id: string) => {
    const section = themeSections.find(s => s.id === id);

    if (!section || !section.theme) {
      setTheme(null);
      return;
    }

    if (section.theme === 'light' || section.theme === 'dark') {
      setTheme(section.theme);
    } else {
      setTheme(null);
    }

    setActiveSection(id);
  };


  return (
    <ThemeContext.Provider value={{ theme, setTheme, activeSection }}>
      {/* Hidden anchor tags for ScrollSpy to monitor */}
      <ScrollSpy
        key="theme-sections"
        rootMargin='300px'
        onChangeActiveId={handleActiveChange}
      >
        {themeSections.map(section => (
          <a
            key={section.id}
            className="hidden"
            href={`#${section.id}`}
            aria-hidden="true"
          />
        ))}
      </ScrollSpy>
      {children}
    </ThemeContext.Provider>
  );
}
